/** In-chat wait for a forwarded Eltern-Mail or a provider confirmation code. */

export const SEEN_EXTRACTION_IDS_KEY = "albis.seenExtractionIds";
export const SEEN_CONFIRMATION_IDS_KEY = "albis.seenForwardingConfirmationIds";
export const AWAITING_FORWARD_KEY = "albis.awaitingForward";
export const AUTO_FORWARD_PROMPT_KEY = "albis.autoForwardPromptShown";

/** @param {string} key */
export function readStoredIds(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String);
  } catch {
    return [];
  }
}

/** @param {string} key @param {Array<string|number>} ids */
export function writeStoredIds(key, ids) {
  try {
    const unique = [];
    const seen = new Set();
    for (const id of ids || []) {
      const value = String(id || "");
      if (!value || seen.has(value)) continue;
      seen.add(value);
      unique.push(value);
    }
    localStorage.setItem(key, JSON.stringify(unique.slice(-80)));
  } catch {
    /* ignore */
  }
}

export function isAutoForwardPromptShown() {
  try {
    return localStorage.getItem(AUTO_FORWARD_PROMPT_KEY) === "1";
  } catch {
    return false;
  }
}

export function markAutoForwardPromptShown() {
  try {
    localStorage.setItem(AUTO_FORWARD_PROMPT_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isAwaitingForward() {
  try {
    return localStorage.getItem(AWAITING_FORWARD_KEY) === "1";
  } catch {
    return false;
  }
}

export function markAwaitingForward() {
  try {
    localStorage.setItem(AWAITING_FORWARD_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function clearAwaitingForward() {
  try {
    localStorage.removeItem(AWAITING_FORWARD_KEY);
  } catch {
    /* ignore */
  }
}

/** @param {object|null|undefined} row */
export function isTerminalExtraction(row) {
  const status = row?.processingStatus || "ready";
  return status === "ready" || status === "failed";
}

/** @param {object|null|undefined} row */
export function isPendingExtraction(row) {
  const status = row?.processingStatus || "";
  return status === "pending_attachments" || status === "processing_attachments";
}

/**
 * First visit seeds ids already on the account so history does not replay.
 * Later visits return at most one unseen confirmation and one unseen mail.
 * Items are newest-first, matching GET /api/school-emails.
 *
 * @param {{
 *   items?: object[],
 *   confirmations?: object[],
 *   seenExtractionIds?: string[] | null,
 *   seenConfirmationIds?: string[] | null,
 * }} [args]
 */
export function planInboundSync({
  items = [],
  confirmations = [],
  seenExtractionIds,
  seenConfirmationIds,
} = {}) {
  const terminal = (items || []).filter((row) => row?.id && isTerminalExtraction(row));
  if (seenExtractionIds == null) {
    return {
      seedExtractionIds: terminal.map((row) => String(row.id)),
      seedConfirmationIds: (confirmations || []).map((row) => String(row?.id || "")).filter(Boolean),
      confirmation: null,
      extraction: null,
      pending: false,
    };
  }

  const seenMail = new Set(seenExtractionIds.map(String));
  const extraction = terminal.find((row) => !seenMail.has(String(row.id))) || null;
  const seenConf = new Set((seenConfirmationIds || []).map(String));
  const confirmation =
    (confirmations || []).find((row) => row?.id && !seenConf.has(String(row.id))) || null;
  const pending =
    !extraction &&
    (items || []).some(
      (row) => row?.id && isPendingExtraction(row) && !seenMail.has(String(row.id)),
    );

  return {
    seedExtractionIds: null,
    seedConfirmationIds: null,
    confirmation,
    extraction,
    pending,
  };
}

/** @param {string|null|undefined} url */
export function safeHttpsUrl(url) {
  try {
    const parsed = new URL(String(url || ""));
    if (parsed.protocol !== "https:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
}
